import { useEffect, useRef, useState } from "react";
import { ApiError, type GrantPreview } from "@tovi/shared";
import { Avatar, Button, QrCode, resolveErrorMessage, useTranslate } from "@tovi/ui";
import {
  identifyLink,
  pollIdentifyCode,
  requestIdentifyCode,
  type IdentifyCode,
} from "@/lib/identify.ts";
import { choicesFor, isNotice, type ActionChoice, type Notice } from "@/lib/grant-choices.ts";
import { clockByGrant, previewGrant, signInWithGrant } from "@/lib/staff.ts";
import { useEmployeePhoto } from "@/lib/use-employee-photo.ts";

/**
 * The one way onto the till: scan a QR with the staff app (`#212`). The
 * keypad `pin.tsx` built is gone — every employee now carries a staff-app
 * credential of their own (issues 242, 243), so a PIN typed in front of a
 * room was a second, weaker credential doing a job something else already
 * does.
 *
 * Layout is a judgement call, not a capture: the till was built from the
 * written spec and there are no screenshots to check it against
 * (`CLAUDE.md` § Known caveats). `ScanPanel` fills the screen alone rather
 * than sitting in a column, since there is nothing left for it to sit beside.
 */
export function IdentifyScreen({ onIdentified }: { onIdentified: () => void }) {
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <ScanPanel onIdentified={onIdentified} />
    </div>
  );
}

const STAFF_URL = import.meta.env.VITE_STAFF_URL as string | undefined;

function groupUserCode(code: string): string {
  return code.match(/.{1,4}/g)?.join("-") ?? code;
}

/**
 * What the scan panel is showing right now, once a grant has been redeemed
 * (`#229`). `code` is the everyday state — the QR and its poll loop, exactly
 * as `#212` shipped it; the other three are what a preview that names more
 * than a bare "take" opens onto.
 */
type Panel =
  | { kind: "code" }
  | { kind: "choice"; preview: GrantPreview; accessToken: string }
  | { kind: "jobs"; preview: GrantPreview; accessToken: string; takeTill: boolean }
  | { kind: "confirm"; text: "checkedIn" | "checkedOut" };

/**
 * The identify screen's one panel: a QR to scan, or why there isn't one.
 *
 * Two unavailable states, and they are different. `VITE_STAFF_URL` unset is a
 * build-time fact — retrying cannot fix it, so that state shows a message and
 * no button. The first code request failing is a runtime fact — offline, or
 * the staff app unreachable — so that one offers a retry that asks for a
 * fresh code.
 *
 * The poll loop runs outside React state — a `setTimeout` chain closed over
 * `cancelled` — because its pace changes at runtime (`slow_down`) and it must
 * stop cleanly on unmount without re-subscribing on every render. `t` and
 * `onIdentified` are read through refs rather than listed as effect
 * dependencies, so a locale change or a new callback identity mid-poll never
 * restarts the chain and abandons a code someone may already be looking at.
 * `restartToken` is the one dependency that *is* meant to restart it —
 * `restart()` bumps it whenever the panel needs a fresh code: Cancel, the
 * 60-second idle return, a confirmation timing out, or a refusal whose reason
 * says the grant itself is gone.
 *
 * A granted scan no longer signs the till straight in. It previews the grant
 * first (`previewGrant`, which spends nothing) and asks `choicesFor` what
 * that preview is worth offering. When the answer is "take" and nothing
 * else — a store with no timeclock, the everyday case — this behaves exactly
 * as it always did: `signInWithGrant`, then `onIdentified()`, no panel ever
 * shown. Anything wider replaces the QR with a choice panel instead.
 */
function ScanPanel({ onIdentified }: { onIdentified: () => void }) {
  const t = useTranslate();
  const [code, setCode] = useState<IdentifyCode | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>({ kind: "code" });
  const [restartToken, setRestartToken] = useState(0);
  const [pendingAction, setPendingAction] = useState<ActionChoice | null>(null);

  const tRef = useRef(t);
  const onIdentifiedRef = useRef(onIdentified);
  useEffect(() => {
    tRef.current = t;
    onIdentifiedRef.current = onIdentified;
  });

  function restart(): void {
    setPanel({ kind: "code" });
    setMessage(null);
    setPendingAction(null);
    setRestartToken((n) => n + 1);
  }

  // Unspent refusals stay on the panel the person is looking at — the grant
  // still works for the other button. Only `grantNotRecognised` (the grant
  // expired while they decided, or a second till already spent it) means
  // there is nothing left to offer, so that one alone returns to a fresh code.
  function refused(caught: unknown): void {
    if (caught instanceof ApiError && caught.reason === "grantNotRecognised") {
      restart();
      return;
    }
    setMessage(resolveErrorMessage(caught, tRef.current));
  }

  async function clockIn(accessToken: string, jobId: string, takeTill: boolean): Promise<void> {
    setPendingAction(takeTill ? "checkInAndTake" : "checkIn");
    setMessage(null);
    try {
      const { cashier } = await clockByGrant({ accessToken, action: "in", jobId, takeTill });
      if (cashier) onIdentifiedRef.current();
      else setPanel({ kind: "confirm", text: "checkedIn" });
    } catch (caught) {
      refused(caught);
    } finally {
      setPendingAction(null);
    }
  }

  async function choose(
    action: ActionChoice,
    preview: GrantPreview,
    accessToken: string,
  ): Promise<void> {
    setMessage(null);

    if (action === "take") {
      setPendingAction("take");
      try {
        await signInWithGrant(accessToken);
        onIdentifiedRef.current();
      } catch (caught) {
        refused(caught);
      } finally {
        setPendingAction(null);
      }
      return;
    }

    if (action === "checkOut") {
      setPendingAction("checkOut");
      try {
        await clockByGrant({ accessToken, action: "out", takeTill: false });
        setPanel({ kind: "confirm", text: "checkedOut" });
      } catch (caught) {
        refused(caught);
      } finally {
        setPendingAction(null);
      }
      return;
    }

    // checkIn or checkInAndTake: exactly one job is chosen silently when
    // there is only one, otherwise the job list decides first.
    const takeTill = action === "checkInAndTake";
    if (preview.jobs.length > 1) {
      setPanel({ kind: "jobs", preview, accessToken, takeTill });
      return;
    }
    await clockIn(accessToken, preview.jobs[0]!.id, takeTill);
  }

  useEffect(() => {
    if (!STAFF_URL) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function requestFreshCode(): Promise<void> {
      let fresh: IdentifyCode;
      try {
        fresh = await requestIdentifyCode();
      } catch {
        if (!cancelled) setUnavailable(true);
        return;
      }
      if (cancelled) return;
      setMessage(null);
      setCode(fresh);
      poll(fresh, fresh.intervalMs);
    }

    function poll(active: IdentifyCode, intervalMs: number): void {
      timer = setTimeout(async () => {
        if (cancelled) return;
        const result = await pollIdentifyCode(active.deviceCode);
        if (cancelled) return;

        switch (result.kind) {
          case "pending":
          case "offline":
            poll(active, intervalMs);
            return;
          case "slow_down":
            // RFC 8628 § 3.5: back off for the rest of this code's life.
            poll(active, intervalMs + 5000);
            return;
          case "expired":
            void requestFreshCode();
            return;
          case "denied":
            setMessage(tRef.current("pos.identify.denied"));
            void requestFreshCode();
            return;
          case "granted":
            try {
              const preview = await previewGrant(result.accessToken);
              const choices = choicesFor(preview);
              const notice = choices.find(isNotice) ?? null;
              const actions = choices.filter((choice): choice is ActionChoice => !isNotice(choice));
              if (notice === null && actions.length === 1 && actions[0] === "take") {
                await signInWithGrant(result.accessToken);
                onIdentifiedRef.current();
                return;
              }
              setPanel({ kind: "choice", preview, accessToken: result.accessToken });
            } catch (caught) {
              if (cancelled) return;
              setMessage(resolveErrorMessage(caught, tRef.current));
              void requestFreshCode();
            }
        }
      }, intervalMs);
    }

    void requestFreshCode();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [restartToken]);

  // A till must not leave a person's name up for the next one in the queue.
  useEffect(() => {
    if (panel.kind !== "choice" && panel.kind !== "jobs") return;
    const timer = setTimeout(restart, 60_000);
    return () => clearTimeout(timer);
  }, [panel]);

  useEffect(() => {
    if (panel.kind !== "confirm") return;
    const timer = setTimeout(restart, 3000);
    return () => clearTimeout(timer);
  }, [panel]);

  if (!STAFF_URL) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 border-line bg-surface p-6 text-center">
        <p className="text-sm text-ink-muted">{t("pos.identify.notConfigured")}</p>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 border-line bg-surface p-6 text-center">
        <p className="text-sm text-ink-muted">{t("pos.identify.unavailable")}</p>
        <Button
          variant="secondary"
          onClick={() => {
            setUnavailable(false);
            restart();
          }}
        >
          {t("pos.identify.retry")}
        </Button>
      </div>
    );
  }

  if (panel.kind === "confirm") {
    return <ConfirmPanel text={panel.text} t={t} />;
  }

  if (panel.kind === "jobs") {
    return (
      <JobsPanel
        preview={panel.preview}
        busy={pendingAction !== null}
        message={message}
        onSelect={(jobId) => void clockIn(panel.accessToken, jobId, panel.takeTill)}
        onCancel={restart}
        t={t}
      />
    );
  }

  if (panel.kind === "choice") {
    const choices = choicesFor(panel.preview);
    const notice = choices.find(isNotice) ?? null;
    const actions = choices.filter((choice): choice is ActionChoice => !isNotice(choice));
    return (
      <ChoicePanel
        preview={panel.preview}
        actions={actions}
        notice={notice}
        pendingAction={pendingAction}
        message={message}
        onChoose={(action) => void choose(action, panel.preview, panel.accessToken)}
        onCancel={restart}
        t={t}
      />
    );
  }

  if (!code) return null;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 border-line bg-surface p-6 text-center">
      <div>
        <h2 className="text-lg font-semibold text-ink-body">{t("pos.identify.heading")}</h2>
        <p className="mt-1 text-sm text-ink-muted">{t("pos.identify.instruction")}</p>
      </div>
      <QrCode
        value={identifyLink(STAFF_URL!, code.userCode)}
        size={176}
        title={t("pos.identify.heading")}
      />
      <p className="text-sm text-ink-muted">
        {t("pos.identify.codeLabel")}{" "}
        <span className="font-mono text-base text-ink-body">{groupUserCode(code.userCode)}</span>
      </p>
      {message ? <p className="text-sm text-danger">{message}</p> : null}
    </div>
  );
}

/**
 * What a redeemed grant is worth beyond just taking the till — naming the
 * employee, the notice if the preview carries one, and one button per choice.
 *
 * Layout is a judgement call, like `IdentifyScreen`'s own comment above: no
 * captures exist to check it against. Buttons stack full-width in a
 * `max-w-xs` column rather than sitting in a row, because three of them at
 * that width must wrap in Khmer, not clip.
 */
function ChoicePanel({
  preview,
  actions,
  notice,
  pendingAction,
  message,
  onChoose,
  onCancel,
  t,
}: {
  preview: GrantPreview;
  actions: ActionChoice[];
  notice: Notice | null;
  pendingAction: ActionChoice | null;
  message: string | null;
  onChoose: (action: ActionChoice) => void;
  onCancel: () => void;
  t: ReturnType<typeof useTranslate>;
}) {
  const photo = useEmployeePhoto(preview.employee.id, preview.employee.photoVersion);
  const busy = pendingAction !== null;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 border-line bg-surface p-6 text-center">
      <Avatar name={preview.employee.name} src={photo ?? undefined} size={64} />
      <p className="text-lg font-semibold text-ink-body">{preview.employee.name}</p>
      {notice ? (
        <p className="text-sm text-ink-muted">{t(`pos.identify.choice.${notice}`)}</p>
      ) : null}
      <div className="flex w-full max-w-xs flex-col gap-3">
        {actions.map((action) => (
          <Button
            key={action}
            variant={action === "take" ? "secondary" : "primary"}
            loading={pendingAction === action}
            disabled={busy}
            onClick={() => onChoose(action)}
          >
            {t(`pos.identify.choice.${action}`)}
          </Button>
        ))}
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          {t("pos.identify.choice.cancel")}
        </Button>
      </div>
      {message ? <p className="text-sm text-danger">{message}</p> : null}
    </div>
  );
}

/** The job list a check-in with more than one enabled job opens onto. */
function JobsPanel({
  preview,
  busy,
  message,
  onSelect,
  onCancel,
  t,
}: {
  preview: GrantPreview;
  busy: boolean;
  message: string | null;
  onSelect: (jobId: string) => void;
  onCancel: () => void;
  t: ReturnType<typeof useTranslate>;
}) {
  const photo = useEmployeePhoto(preview.employee.id, preview.employee.photoVersion);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 border-line bg-surface p-6 text-center">
      <Avatar name={preview.employee.name} src={photo ?? undefined} size={64} />
      <p className="text-lg font-semibold text-ink-body">{preview.employee.name}</p>
      <p className="text-sm text-ink-muted">{t("pos.identify.choice.jobHeading")}</p>
      <div className="flex w-full max-w-xs flex-col gap-3">
        {preview.jobs.map((job) => (
          <Button key={job.id} variant="secondary" disabled={busy} onClick={() => onSelect(job.id)}>
            {job.name}
          </Button>
        ))}
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          {t("pos.identify.choice.cancel")}
        </Button>
      </div>
      {message ? <p className="text-sm text-danger">{message}</p> : null}
    </div>
  );
}

/**
 * "Checked in." or "Checked out." for about three seconds, then back to a
 * fresh code (`ScanPanel`'s own confirm-timeout effect). No time of day: the
 * till has no store-timezone formatter to show one with.
 */
function ConfirmPanel({
  text,
  t,
}: {
  text: "checkedIn" | "checkedOut";
  t: ReturnType<typeof useTranslate>;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 border-line bg-surface p-6 text-center">
      <p className="text-lg font-semibold text-ink-body">{t(`pos.identify.choice.${text}`)}</p>
    </div>
  );
}
